import { useState, useEffect, useMemo } from "react";
import {
  Search,
  Plus,
  Minus,
  Wrench,
  Award,
  CheckCircle,
  Heart,
  Briefcase,
  Activity,
  FileText,
  Users,
  Loader2,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { SitePagination } from "../ui/SitePagination";
import {
  vendorsAPI,
  parseVendorCategory,
  publicInstitutionsAPI,
  type VendorListItem,
  type VendorDetailItem,
} from "../../services/api";

const VENDORS_PER_PAGE = 6; // 2 rows × 3 columns on lg

export default function VendorsPage() {
  const [vendorList, setVendorList] = useState<VendorListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [vendorDetail, setVendorDetail] = useState<VendorDetailItem | null>(
    null
  );
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [vendorsListLoaded, setVendorsListLoaded] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / VENDORS_PER_PAGE));

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [expandedFilters, setExpandedFilters] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  // Reset to list view when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStates, selectedCities, selectedCategories]);

  // Fetch States on mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const states = await publicInstitutionsAPI.getStates();
        setAvailableStates(states);
      } catch (err) {
        console.error("Failed to fetch states:", err);
      }
    };
    void fetchStates();
  }, []);

  // Fetch Cities when States change
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const cities = await publicInstitutionsAPI.getCities(
          selectedStates.length === 1 ? selectedStates[0] : undefined
        );
        setAvailableCities(cities);
        setSelectedCities(prev => prev.filter(city => cities.includes(city)));
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      }
    };
    void fetchCities();
  }, [selectedStates]);

  const toggleFilterExpansion = (filterName: string) => {
    setExpandedFilters(prev =>
      prev.includes(filterName)
        ? prev.filter(f => f !== filterName)
        : [...prev, filterName]
    );
  };

  const resetFilters = () => {
    setSearchQuery("");
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedCategories([]);
  };

  const filterOptions = useMemo(() => {
    return {
      states: availableStates,
      cities: availableCities,
      categories: [
        "Plumbing",
        "Electrical",
        "Carpentry",
        "Housekeeping",
        "Security",
        "Catering",
        "Stationery",
        "IT Support",
        "Civil Construction",
        "Pest Control",
        "Laundry",
      ],
    };
  }, [availableStates, availableCities]);

  // Fetch vendor list when page changes (server-side pagination)
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingVendors(true);
      setVendorError(null);
      try {
        const params: Record<string, unknown> = {
          page: currentPage,
          page_size: VENDORS_PER_PAGE,
        };
        if (searchQuery.trim()) {
          params.search = searchQuery.trim();
        }
        if (selectedStates.length > 0) {
          params.state = selectedStates;
        }
        if (selectedCities.length > 0) {
          params.city = selectedCities;
        }
        if (selectedCategories.length > 0) {
          params.category = selectedCategories;
        }

        const result = await vendorsAPI.getVendorList(params);
        if (!cancelled) {
          setVendorList(Array.isArray(result.data) ? result.data : []);
          setTotalCount(typeof result.count === "number" ? result.count : 0);
        }
      } catch {
        if (!cancelled) {
          setVendorError("Failed to load vendors.");
          setVendorList([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoadingVendors(false);
          setVendorsListLoaded(true);
        }
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    currentPage,
    searchQuery,
    selectedStates,
    selectedCities,
    selectedCategories,
  ]);

  const openVendorDetail = async (id: number) => {
    setDetailModalOpen(true);
    setVendorDetail(null);
    setDetailError(null);
    setLoadingDetail(true);
    try {
      const detail = await vendorsAPI.getVendorDetail(String(id));
      setVendorDetail(detail);
    } catch {
      setDetailError("Failed to load vendor details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const expertCategories = [
    {
      icon: <Heart className="h-10 w-6" />,
      title: "Nutritionists",
      description: "Diet planning and nutritional guidance",
      specializations: [
        "Diet planning",
        "Nutrition counseling",
        "Menu design",
        "Health assessment",
      ],
    },
    {
      icon: <Activity className="h-6 w-6" />,
      title: "Medical Doctors",
      description: "Healthcare and medical consultation",
      specializations: [
        "General medicine",
        "Emergency care",
        "Health check-ups",
        "Preventive care",
      ],
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Sports Coaches",
      description: "Physical fitness and sports training",
      specializations: [
        "Fitness training",
        "Yoga",
        "Team sports",
        "Athletic development",
      ],
    },
    {
      icon: <Briefcase className="h-6 w-6" />,
      title: "Career Mentors",
      description: "Career guidance and professional development",
      specializations: [
        "Career counseling",
        "Resume building",
        "Interview prep",
        "Skill development",
      ],
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: "Mental Health Counselors",
      description: "Psychological support and counseling",
      specializations: [
        "Counseling",
        "Stress management",
        "Peer support",
        "Crisis intervention",
      ],
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative flex min-h-[40vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1683372101272-6a144c795e2a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBjb250cmFjdG9ycyUyMGJ1aWxkaW5nfGVufDF8fHx8MTc2NTE5NTk2M3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Construction contractors"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A1E1E]/70 to-[#8B2E2E]/60" />
        </div>

        <div className="relative z-10 mx-auto mb-10 mt-10 max-w-[1400px] px-4 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-5xl lg:text-6xl">
              Vendors, Contractors & Expert Directories
            </h1>
            <p className="text-2xl leading-relaxed text-gray-100">
              Connecting verified service providers with Jain hostels across
              India. Quality assurance, transparent processes, and fair
              opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* Vendor Registry */}
      <section className="bg-[#FFF9F0] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row">
            {/* Left Sidebar Filters */}
            <div className="w-full shrink-0 lg:w-[320px]">
              <div className="sticky top-40 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="mb-3 text-[22px] font-semibold text-[#7A1E1E]">
                    Filters For Vendor
                  </h3>
                  <button
                    onClick={resetFilters}
                    className="pointer-events-auto text-[15px] text-[#D4A017] underline decoration-1 underline-offset-4 hover:text-[#B8900F]"
                  >
                    Reset Filters
                  </button>
                  <div className="mt-4 h-[1px] w-full bg-gray-200" />
                </div>

                <div className="mb-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search Name or City..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full rounded border border-gray-300 py-2 pl-9 pr-4 text-[15px] focus:border-transparent focus:ring-1 focus:ring-[#7A1E1E]"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  {/* Common styles for Accordions */}
                  {[
                    {
                      title: "Vendor Category",
                      options: filterOptions.categories,
                      selected: selectedCategories,
                      setter: setSelectedCategories,
                    },
                    {
                      title: "State selection",
                      options: filterOptions.states,
                      selected: selectedStates,
                      setter: setSelectedStates,
                    },
                    // {
                    //   title: "City Selection Based On State",
                    //   options: filterOptions.cities,
                    //   selected: selectedCities,
                    //   setter: setSelectedCities,
                    // },
                  ].map(({ title, options, selected, setter }) => (
                    <div key={title} className="border-b border-gray-100 py-3">
                      <button
                        onClick={() => toggleFilterExpansion(title)}
                        className="flex w-full items-center justify-between text-left font-semibold text-gray-900 hover:text-[#7A1E1E]"
                      >
                        <span className="text-[16px]">{title}</span>
                        {expandedFilters.includes(title) ? (
                          <Minus className="h-4 w-4 text-[#7A1E1E]" />
                        ) : (
                          <Plus className="h-4 w-4 text-[#7A1E1E]" />
                        )}
                      </button>
                      {expandedFilters.includes(title) &&
                        options.length > 0 && (
                          <div className="mt-3 flex max-h-48 flex-col gap-2 overflow-y-auto pr-1">
                            {options.map((option: string) => {
                              return (
                                <label
                                  key={option}
                                  className="flex cursor-pointer items-start gap-3 py-1"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected.includes(option)}
                                    onChange={() => {
                                      setter((prev: string[]) =>
                                        prev.includes(option)
                                          ? prev.filter(
                                            (v: string) => v !== option
                                          )
                                          : [...prev, option]
                                      );
                                    }}
                                    className="mt-[2px] h-[18px] w-[18px] rounded-sm border-gray-400 text-[#7A1E1E] focus:ring-[#7A1E1E]"
                                  />
                                  <span className="text-[14px] leading-tight text-gray-700">
                                    {option}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      {expandedFilters.includes(title) &&
                        options.length === 0 && (
                          <div className="mt-3 text-[14px] italic text-gray-500">
                            No options available
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Content - Cards Grid */}
            <div className="flex-1">
              <div className="mb-8">
                <h2 className="mb-2 text-3xl text-[#7A1E1E] sm:text-4xl">
                  Approved Vendor Registry
                </h2>
                <div className="mb-4 h-1 w-24 bg-[#D4A017]" />
                <p className="text-lg text-gray-700">
                  {loadingVendors
                    ? "Loading vendors..."
                    : `Found ${totalCount} Vendors`}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  All vendors are verified, rated, and monitored for quality and
                  compliance.
                </p>
              </div>

              {loadingVendors ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-12 w-12 animate-spin text-[#7A1E1E]" />
                </div>
              ) : vendorError ? (
                <div className="py-12 text-center">
                  <p className="text-lg text-red-600">{vendorError}</p>
                </div>
              ) : vendorList.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-gray-600">
                    No vendors available at the moment.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {vendorList.map(vendor => (
                      <div
                        key={vendor.id}
                        className="flex flex-col rounded-xl border border-l-4 border-gray-200 border-l-[#D4A017] bg-white p-6 shadow-md transition-shadow hover:shadow-lg"
                      >
                        <div className="mb-4 flex items-start gap-4">
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                            {vendor.logo ? (
                              <ImageWithFallback
                                src={vendor.logo}
                                alt={vendor.vendor_name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Wrench className="h-7 w-7" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-bold text-[#7A1E1E]">
                              {vendor.vendor_name}
                            </h3>
                            <p className="mt-0.5 text-sm text-gray-500">
                              {vendor.city ?? "—"}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {parseVendorCategory(vendor.category)}
                            </p>
                          </div>
                        </div>
                        {vendor.short_description ? (
                          <p className="mb-3 line-clamp-2 text-sm text-gray-600">
                            {vendor.short_description}
                          </p>
                        ) : null}
                        <div className="mb-4 flex items-center gap-2 text-sm text-amber-600">
                          <CheckCircle className="h-4 w-4 shrink-0" />
                          <span>
                            {vendor.verification_status === "verified" ||
                              vendor.verification_status === "pending"
                              ? "Verified"
                              : (vendor.verification_status ?? "Verified")}
                          </span>
                        </div>
                        <div className="mt-auto flex justify-end">
                          <button
                            type="button"
                            onClick={() => openVendorDetail(vendor.id)}
                            className="inline-flex items-center gap-1 rounded-md bg-[#7A1E1E] px-4 py-2 text-sm font-medium text-white transition-all hover:bg-[#5A1515] hover:text-white"
                          >
                            View More
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {vendorsListLoaded && vendorList.length > 0 && (
                    <div className="mt-10">
                      <SitePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        pageSize={VENDORS_PER_PAGE}
                        onPageChange={setCurrentPage}
                        itemLabel="vendors"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Expert Directory */}
      <section className="bg-white bg-gradient-to-t from-white to-[#fff5e6ec] py-10 lg:py-12">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-14 rounded-2xl border-l-8 border-[#7A1E1E] bg-white p-8">
            <h3 className="mb-4 text-2xl text-[#7A1E1E]">
              Vendor Profile Includes:
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                "Verification status and credentials",
                "Past projects and portfolio",
                "Customer feedback & ratings",
                "Compliance documents",
                "Contact details and availability",
                "Service areas and specializations",
              ].map(item => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 text-[#D4A017]" />
                  <span className="text-gray-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Expert Directory
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Wellbeing, Diet, Career, and Sports experts empanelled for hostel
              services.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-5">
            {expertCategories.map(expert => (
              <div
                key={expert.title}
                className="overflow-hidden rounded-xl bg-white shadow-lg transition-all hover:shadow-2xl"
              >
                <div className="bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] px-4 py-6 text-center text-white">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    {expert.icon}
                  </div>
                  <h3 className="mb-2 text-lg">{expert.title}</h3>
                  <p className="text-sm text-gray-200">{expert.description}</p>
                </div>
                <div className="p-6">
                  {/* <h4 className="mb-3 text-sm text-gray-600">
                    Specializations:
                  </h4> */}
                  <ul className="space-y-2">
                    {expert.specializations.map(spec => (
                      <li
                        key={spec}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <div className="h-1.5 w-1.5 rounded-full bg-[#D4A017]" />
                        {spec}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contractor Management */}
      <section className="mb-4 bg-white py-10 lg:py-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl text-[#7A1E1E] sm:text-4xl">
              Contractor Management
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-[#D4A017]" />
            <p className="mx-auto max-w-3xl text-lg text-gray-700">
              Streamlined processes for tender notifications, work orders, and
              project completion.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                icon: <FileText className="h-6 w-6" />,
                title: "Tender Notifications",
                description:
                  "Real-time alerts for new project opportunities matching your expertise",
                features: [
                  "Email notifications",
                  "SMS alerts",
                  "Dashboard updates",
                  "Document access",
                ],
              },
              {
                icon: <Briefcase className="h-6 w-6" />,
                title: "Work-Order Tracking",
                description:
                  "Complete visibility into project status and milestones",
                features: [
                  "Project dashboard",
                  "Milestone tracking",
                  "Payment schedules",
                  "Communication hub",
                ],
              },
              {
                icon: <Award className="h-6 w-6" />,
                title: "Completion & Certification",
                description:
                  "Digital certification and performance evaluation upon completion",
                features: [
                  "Quality assessment",
                  "Performance rating",
                  "Digital certificates",
                  "Portfolio building",
                ],
              },
            ].map(process => (
              <div
                key={process.title}
                className="rounded-xl bg-gradient-to-br from-[#FFF9F0] to-[#FFF5E6] p-6 shadow-lg transition-all hover:shadow-xl"
              >
                <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                  {process.icon}
                </div>
                <h3 className="mb-3 text-center text-xl text-[#7A1E1E]">
                  {process.title}
                </h3>
                <p className="mb-4 text-center text-sm text-gray-700">
                  {process.description}
                </p>
                <ul className="space-y-2">
                  {process.features.map(feature => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <CheckCircle className="h-4 w-4 flex-shrink-0 text-[#D4A017]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor detail modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden border-gray-200 bg-white p-0 shadow-xl">
          <DialogHeader className="sr-only">
            <DialogTitle>Vendor details</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
              </div>
            ) : detailError ? (
              <div className="px-6 py-12 text-center">
                <p className="text-red-600">{detailError}</p>
              </div>
            ) : vendorDetail ? (
              <div className="p-6">
                <div className="mb-6 flex items-start gap-4 border-b border-gray-100 pb-6">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white">
                    {vendorDetail.logo ? (
                      <ImageWithFallback
                        src={vendorDetail.logo}
                        alt={vendorDetail.vendor_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Wrench className="h-10 w-10" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#7A1E1E]">
                      {vendorDetail.vendor_name}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {vendorDetail.company_name ?? "—"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {vendorDetail.city ?? "—"}
                      {vendorDetail.state ? `, ${vendorDetail.state}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {parseVendorCategory(vendorDetail.category)}
                    </p>
                  </div>
                </div>
                {vendorDetail.short_description ? (
                  <div className="mb-4">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      About
                    </p>
                    <p className="text-gray-700">
                      {vendorDetail.short_description}
                    </p>
                  </div>
                ) : null}
                <div className="space-y-4">
                  {(vendorDetail.contact_person_name ??
                    vendorDetail.mobile_number ??
                    vendorDetail.email) && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Contact
                        </p>
                        <div className="space-y-1.5 text-sm text-gray-700">
                          {vendorDetail.contact_person_name && (
                            <p>{vendorDetail.contact_person_name}</p>
                          )}
                          {vendorDetail.mobile_number && (
                            <p className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5" />
                              {vendorDetail.mobile_number}
                            </p>
                          )}
                          {vendorDetail.email && (
                            <p className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5" />
                              {vendorDetail.email}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  {vendorDetail.office_address && (
                    <div className="flex items-start gap-2 text-sm text-gray-700">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                      <span>{vendorDetail.office_address}</span>
                    </div>
                  )}
                  {vendorDetail.specialization && (
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Specialization
                      </p>
                      <p className="text-sm text-gray-700">
                        {vendorDetail.specialization}
                      </p>
                    </div>
                  )}
                  {vendorDetail.years_of_experience !== null &&
                    vendorDetail.years_of_experience !== undefined && (
                      <div>
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Years of Experience
                        </p>
                        <p className="text-sm text-gray-700">
                          {vendorDetail.years_of_experience}
                        </p>
                      </div>
                    )}
                  {vendorDetail.service_timings && (
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Service Timings
                      </p>
                      <p className="text-sm text-gray-700">
                        {vendorDetail.service_timings}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                      <CheckCircle className="h-3.5 w-3.5" />
                      {vendorDetail.verification_status ?? "Verified"}
                    </span>
                    {vendorDetail.available_for_work && (
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                        Available for work
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
